import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const SORT_FIELDS = {
  first_name: 'text',
  title: 'text',
  company_name: 'text',
  email: 'text',
  phone: 'text',
  status: 'text',
  source: 'text',
};

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [sortField, setSortField] = useState('first_name');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    title: '', company_id: '', status: 'lead', notes: ''
  });
  const navigate = useNavigate();

  const load = (q = '') => {
    setLoading(true);
    api.get(`/contacts?search=${encodeURIComponent(q)}&limit=200`)
      .then(r => { setContacts(r.data.contacts); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/accounts?limit=100').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
  }, []);

  const handleSearch = e => {
    setSearch(e.target.value);
    clearTimeout(window._cst);
    window._cst = setTimeout(() => load(e.target.value), 350);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = [...contacts].sort((a, b) => {
    let valA = sortField === 'first_name'
      ? `${a.first_name} ${a.last_name}`.toLowerCase()
      : (a[sortField] || '').toLowerCase();
    let valB = sortField === 'first_name'
      ? `${b.first_name} ${b.last_name}`.toLowerCase()
      : (b[sortField] || '').toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSave = async () => {
    setError('');
    if (!form.first_name.trim()) return setError('First name is required.');
    if (!form.last_name.trim()) return setError('Last name is required.');
    setSaving(true);
    try {
      await api.post('/contacts', form);
      setShowForm(false);
      setForm({ first_name:'', last_name:'', email:'', phone:'', title:'', company_id:'', status:'lead', notes:'' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = s => {
    const map = { active:'badge-green', lead:'badge-amber', customer:'badge-blue', cold:'badge-gray', inactive:'badge-gray' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
  };

  const sourceBadge = s => {
    const map = { outlook:'badge-blue', salesforce:'badge-amber', manual:'badge-gray', import:'badge-gray' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'var(--border-md)', marginLeft:4 }}>↕</span>;
    return <span style={{ color:'var(--accent)', marginLeft:4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => handleSort(field)}
      style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      {children}<SortIcon field={field} />
    </th>
  );

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{total} contacts</div>
        <div style={{ display:'flex', gap:8 }}>
          <input
            value={search} onChange={handleSearch} placeholder="Search contacts…"
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none', width:200 }}
          />
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setError(''); }}>
            + Add contact
          </button>
        </div>
      </div>

      {/* Add Contact Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>New contact</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">First name *</label>
              <input className="form-input" value={form.first_name}
                onChange={e => setForm({...form, first_name:e.target.value})}
                placeholder="First name" />
            </div>
            <div>
              <label className="form-label">Last name *</label>
              <input className="form-input" value={form.last_name}
                onChange={e => setForm({...form, last_name:e.target.value})}
                placeholder="Last name" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm({...form, email:e.target.value})}
                placeholder="email@company.com" />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone}
                onChange={e => setForm({...form, phone:e.target.value})}
                placeholder="e.g. 555-123-4567" />
            </div>
            <div>
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title}
                onChange={e => setForm({...form, title:e.target.value})}
                placeholder="e.g. Purchasing Manager" />
            </div>
            <div>
              <label className="form-label">Account</label>
              <select className="form-input" value={form.company_id}
                onChange={e => setForm({...form, company_id:e.target.value})}>
                <option value="">— No account —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="sf-input" value={form.status}
                onChange={e => setForm({...form, status:e.target.value})}>
                <option value="lead">Lead</option>
                <option value="active">Active</option>
                <option value="customer">Customer</option>
                <option value="cold">Cold</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm({...form, notes:e.target.value})}
                placeholder="Optional notes" />
            </div>
          </div>
          {error && <div className="form-error" style={{ marginTop:8 }}>{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save contact'}
            </button>
            <button className="btn" onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      <div className="card" style={{ padding:0 }}>
        {loading
          ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>
          : <table className="data-table">
              <thead>
                <tr>
                  <SortTh field="first_name">Name</SortTh>
                  <SortTh field="title">Title</SortTh>
                  <SortTh field="company_name">Account</SortTh>
                  <SortTh field="email">Email</SortTh>
                  <SortTh field="phone">Phone</SortTh>
                  <SortTh field="status">Status</SortTh>
                  <SortTh field="source">Source</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0
                  ? <tr><td colSpan={7} style={{ padding:24, color:'var(--text-tertiary)', textAlign:'center' }}>
                      No contacts found. Click "+ Add contact" to create one.
                    </td></tr>
                  : sorted.map(c => (
                      <tr key={c.id} style={{ cursor:'pointer' }}
                        onClick={() => navigate(`/contacts/${c.id}`)}>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div className="avatar" style={{ width:28, height:28, fontSize:10 }}>
                              {(c.first_name[0]||'') + (c.last_name[0]||'')}
                            </div>
                            {c.first_name} {c.last_name}
                          </div>
                        </td>
                        <td style={{ color:'var(--text-secondary)' }}>{c.title || '—'}</td>
                        <td style={{ color:'var(--text-secondary)' }}>{c.company_name || '—'}</td>
                        <td style={{ color:'var(--accent-dark)', fontSize:12 }}>{c.email || '—'}</td>
                        <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{c.phone || '—'}</td>
                        <td>{statusBadge(c.status)}</td>
                        <td>{sourceBadge(c.source)}</td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
        }
      </div>
    </>
  );
}